import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {MenuItem} from 'primeng/api';
import {RefundSearchResultView, RefundState, WebRefundService} from 'web-angular-client';
import {ROUTES} from '../../app.routes';
import {AlertService} from '../../sdk/service/alert.service';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {ConfirmationService} from '../../sdk/service/confirmation.service';

/**
 * Shared per-row action logic for refund tables — used by both the refund transactions list and the refund
 * dashboard's recent-refunds preview so the action menu and its handlers live in one place. Builds the context menu
 * (View account / Cancel refund / Copy refund link) and runs the actions; the mutating Cancel takes an
 * {@code onReload} callback so each host refreshes its own view afterwards.
 */
@Injectable({providedIn: 'root'})
export class RefundRowActionsService
{
    constructor(
        private readonly router: Router,
        private readonly webRefundService: WebRefundService,
        private readonly confirmationService: ConfirmationService,
        private readonly alertService: AlertService,
        private readonly authenticationService: AuthenticationService)
    {
    }

    /**
     * Builds a row's context-menu items. "View account" appears only for users who can read accounts; "Cancel refund"
     * appears only for users who can update accounts and is enabled only while the refund is still cancellable
     * (OPEN / INITIATED); "Copy refund link" appears only once ePay has issued a link.
     *
     * @param row the refund row
     * @param onReload invoked after a successful/finalised cancel so the host can refresh its view
     * @return the menu items for that row
     */
    buildMenuItems(row: RefundSearchResultView, onReload: () => void): MenuItem[]
    {
        const items: MenuItem[] = [];
        const isGuestAdmin = this.authenticationService.isGuestAdminUser();

        if (this.authenticationService.hasPrivilege(ROUTES.SECURE.ACCOUNT.VIEW.privilege))
        {
            items.push({label: 'View Account', icon: 'fa fa-eye fa-lg', command: () => this.viewAccount(row)});
        }

        if (!isGuestAdmin && this.authenticationService.hasPrivilege('ACCOUNT_UPDATE'))
        {
            items.push({
                label: 'Cancel Refund',
                icon: 'fa fa-ban fa-lg',
                disabled: !this.isCancellable(row?.refund?.refundState),
                command: () => this.cancelRefund(row, onReload)
            });
        }
        if (!isGuestAdmin && row?.refund?.refundUrl)
        {
            items.push({label: 'Copy Refund Link', icon: 'fa fa-link fa-lg', command: () => this.copyRefundLink(row)});
        }
        return items;
    }

    /** Navigates to the originating account's detail page. */
    viewAccount(row: RefundSearchResultView): void
    {
        const accountId = row?.refund?.accountId;
        if (accountId == null)
        {
            return;
        }
        this.router.navigate([ROUTES.SECURE.ACCOUNT.VIEW.pathSansParameters, accountId]).then();
    }

    /**
     * Cancels a refund after confirmation. On success {@code onReload} is invoked; a 409 means the refund was finalised
     * in the meantime, so we tell the merchant and still reload to show its current state.
     *
     * @param row the refund row
     * @param onReload invoked after a successful or already-finalised (409) cancel
     */
    cancelRefund(row: RefundSearchResultView, onReload: () => void): void
    {
        if (this.authenticationService.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }
        const refundId = row?.refund?.id;
        if (refundId == null || !this.isCancellable(row?.refund?.refundState))
        {
            return;
        }
        this.confirmationService
            .confirm(
                'Cancel Refund', 'Are you sure you want to cancel this refund?', 'Cancel refund', 'Keep refund',
                'md')
            .then(confirmed =>
            {
                if (!confirmed)
                {
                    return;
                }
                this.webRefundService.cancelRefund(refundId).subscribe({
                    next: () =>
                    {
                        this.alertService.success('Refund cancelled.');
                        onReload();
                    },
                    error: error =>
                    {
                        if (error?.status === 409)
                        {
                            this.alertService
                                .error('This refund has already been finalised and can no longer be cancelled.');
                            onReload();
                        }
                        else
                        {
                            this.alertService.error('Failed to cancel the refund. Please try again.');
                        }
                    }
                });
            })
            .catch(() => this.alertService.error('Failed to start cancellation. Please try again.'));
    }

    /** Copies the customer-facing refund link to the clipboard. */
    copyRefundLink(row: RefundSearchResultView): void
    {
        if (this.authenticationService.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }
        const url = row?.refund?.refundUrl;
        if (!url)
        {
            return;
        }
        if (!navigator.clipboard?.writeText)
        {
            this.alertService.error('Clipboard is not available in this browser.');
            return;
        }
        navigator.clipboard
            .writeText(url)
            .then(() => this.alertService.success('Refund link copied to clipboard.'))
            .catch(() => this.alertService.error('Failed to copy to clipboard.'));
    }

    private isCancellable(state: RefundState | null | undefined): boolean
    {
        return state === RefundState.OPEN || state === RefundState.INITIATED;
    }

    private displayGuestPrivilegeError(): void
    {
        this.alertService.error('Guest accounts cannot perform this action.', false);
    }
}
